import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Quill의 기본 스타일 추가
import { instance } from '../../api/axiosInstance';
import ManagerSidebar from '../../components/ManagerSidebar';
import NoticeComponent from '../../components/NoticeComponent';
import { FaUpload } from 'react-icons/fa6';
import { categories } from '../../types/categories';
import Cookies from 'js-cookie';
import MainFileUpload from './MainFileUpload';
import AdditionalImagesUpload from './AdditionalImagesUpload';
import TextEditor from "../../components/texteditor/TextEditor";

const ProductUploadForm: React.FC = () => {
  const [productData, setProductData] = useState({
    category: '',
    date: new Date().toISOString(),
    star: 1,
    review: 0,
    name: '',
    price: '',
    originalPrice: 0,
    discount: 0,
    isDiscounted: false,
    description: '',
    usageInstructions: '',
    introduction: '',
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedAdditionalFiles, setSelectedAdditionalFiles] = useState<
    (File | null)[]
  >(Array(6).fill(null));
  const [selectedGLBFiles, setSelectedGLBFiles] = useState<File[]>([]);
  const [selectedSkinFile, setSelectedSkinFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 가격과 할인율 포맷 함수
  const formatPrice = (value: string | number) => {
    const price = parseFloat(value.toString());
    if (isNaN(price)) return '';
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(price);
  };

  const formatDiscount = (value: number) => {
    return Math.round(value); // 소수점 제거 후 정수로 반환
  };

  // 상품 가격과 원래 가격이 변경될 때 자동으로 할인가 계산
  useEffect(() => {
    const originalPrice = parseFloat(
      productData.originalPrice.toString().replace(/,/g, '')
    ); // 콤마 제거 후 숫자로 변환
    const price = parseFloat(productData.price.replace(/,/g, ''));

    if (
      !isNaN(originalPrice) &&
      !isNaN(price) &&
      originalPrice > 0 &&
      price > 0
    ) {
      const discount = ((originalPrice - price) / originalPrice) * 100;
      setProductData((prevData) => ({
        ...prevData,
        discount: discount > 0 ? discount : 0, // 음수 방지
      }));
    }
  }, [productData.price, productData.originalPrice]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;

    // 가격 입력 처리 시 천 단위 콤마 추가
    if (name === 'price' || name === 'originalPrice') {
      const formattedValue = value.replace(/[^0-9]/g, ''); // 숫자 이외의 값 제거
      setProductData((prevData) => ({
        ...prevData,
        [name]: formattedValue, // 여기서는 포맷하지 않고, 나중에 숫자로 변환
      }));
    } else {
      setProductData((prevData) => ({
        ...prevData,
        [name]:
          type === 'checkbox'
            ? (event.target as HTMLInputElement).checked
            : value,
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (selectedFiles.length === 0) {
      setErrorMessage('메인 파일은 반드시 포함되어야 합니다.');
      return;
    }

    const additionalFilesCount = selectedAdditionalFiles.filter(
      (file) => file !== null
    ).length;
    if (additionalFilesCount < 1) {
      setErrorMessage('추가 이미지는 최소 1장 이상이어야 합니다.');
      return;
    }

    const data = new FormData();
    const formattedDate = new Date(productData.date)
      .toISOString()
      .replace('Z', '');
    data.append('date', formattedDate);

    Object.keys(productData).forEach((key) => {
      data.append(key, (productData as any)[key]);
    });

    selectedFiles.forEach((file) => {
      data.append('files', file);
    });

    selectedAdditionalFiles.forEach((file) => {
      if (file) {
        data.append('additionalFiles', file);
      }
    });

    selectedGLBFiles.forEach((file) => {
      data.append('glbFiles', file);
    });

    if (selectedSkinFile) {
      data.append('skinFiles', selectedSkinFile);
    }

    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      }

      const response = await instance.post('/auth/products', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Product uploaded successfully!');
      resetForm();
    } catch (error) {
      console.error(error);
      setErrorMessage('Failed to upload product.');
    }
  };

  const resetForm = () => {
    setProductData({
      category: '',
      date: new Date().toISOString(),
      star: 1,
      review: 0,
      name: '',
      price: '',
      originalPrice: 0,
      discount: 0,
      isDiscounted: false,
      description: '',
      usageInstructions: '',
      introduction: '',
    });
    setSelectedFiles([]);
    setSelectedAdditionalFiles(Array(6).fill(null));
    setSelectedGLBFiles([]);
    setSelectedSkinFile(null);
    setErrorMessage(null);
  };

  return (
    <section className="max-w-[2560px] w-full mx-auto mt-[90px] flex min-h-screen">
      <ManagerSidebar />
      <article className="max-w-[1440px] w-full mx-auto py-8 flex-1">
        <NoticeComponent title="업로드 상품" content="" />
        <div className="flex flex-col flex-1 overflow-auto overflow-x-hidden w-full">
          <div className="max-w-[700px] bg-white p-6 rounded-md shadow-md">
            {errorMessage && <p className="text-red-600">{errorMessage}</p>}
            {loading && <p>Loading...</p>}
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block mb-2 font-semibold text-MainTextColor">
                  카테고리
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {categories.map((category) => (
                    <button
                      type="button"
                      key={category.value}
                      onClick={() =>
                        setProductData({
                          ...productData,
                          category: category.value,
                        })
                      }
                      className={`py-2 px-4 font-semibold rounded-md text-sm focus:outline-none ${
                        productData.category === category.value
                          ? 'bg-MainColor text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {category.title}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block mb-1">상품명</label>
                  <input
                    type="text"
                    name="name"
                    value={productData.name}
                    onChange={handleInputChange}
                    className="w-full h-12 px-3 py-2 border rounded-md focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1">상품가격</label>
                  <input
                    type="text"
                    name="price"
                    value={productData.price}
                    onChange={handleInputChange}
                    className="w-full h-12 px-3 py-2 border rounded-md focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block mb-1">원래 가격</label>
                  <input
                    type="text"
                    name="originalPrice"
                    value={productData.originalPrice}
                    onChange={handleInputChange}
                    className="w-full h-12 px-3 py-2 border rounded-md focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block mb-1">할인</label>
                  <input
                    type="text"
                    name="discount"
                    value={`${formatDiscount(productData.discount)}%`} // 할인율에 % 기호 추가
                    className="w-full h-12 px-3 py-2 border rounded-md focus:outline-none"
                    readOnly
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isDiscounted"
                    checked={productData.isDiscounted}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <label>할인 여부</label>
                </div>
              </div>

              <div className="mb-12">
                <label className="block mb-1">상품설명</label>
                <TextEditor
                  value={productData.description}
                  onChange={(content) => setProductData({ ...productData, description: content })}
                />
              </div>

              <div className="mb-12">
                <label className="block mb-1">사용 설명서</label>
                <TextEditor
                  value={productData.usageInstructions}
                  onChange={(content) => setProductData({ ...productData, usageInstructions: content })}
                />
              </div>

              <div className="mb-12">
                <label className="block mb-1">소개</label>
                <TextEditor
                  value={productData.introduction}
                  onChange={(content) => setProductData({ ...productData, introduction: content })}
                />
              </div>

              <MainFileUpload setSelectedFiles={setSelectedFiles} />
              <AdditionalImagesUpload
                selectedAdditionalFiles={selectedAdditionalFiles}
                setSelectedAdditionalFiles={setSelectedAdditionalFiles}
              />

              {(productData.category === 'MODELING' ||
                productData.category === 'ENTITY') && (
                <div className="mb-6">
                  <label className="block mb-2">GLB 모델 파일 업로드</label>
                  <input
                    type="file"
                    onChange={(event) => {
                      if (event.target.files) {
                        setSelectedGLBFiles(Array.from(event.target.files));
                      }
                    }}
                    className="w-full text-sm text-gray-500 cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-MainTextColor file:text-white hover:file:bg-MainColor transition duration-300 ease-in-out"
                    accept=".glb"
                    multiple
                  />
                </div>
              )}

              {productData.category === 'skin' && (
                <div className="mb-6">
                  <label className="block mb-2">
                    스킨 파일 업로드 (64x64 또는 64x32)
                  </label>
                  <input
                    type="file"
                    onChange={(event) => {
                      if (event.target.files) {
                        const file = event.target.files[0];
                        const img = new Image();
                        img.src = URL.createObjectURL(file);
                        img.onload = () => {
                          if (
                            (img.width === 64 && img.height === 64) ||
                            (img.width === 64 && img.height === 32)
                          ) {
                            setSelectedSkinFile(file);
                          } else {
                            setErrorMessage(
                              'PNG 파일은 64x64 또는 64x32 사이즈여야 합니다.'
                            );
                          }
                        };
                      }
                    }}
                    className="w-full text-sm text-gray-500 cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition duration-300 ease-in-out"
                    accept=".png"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full mt-6 bg-black text-white font-bold text-sm py-3 rounded-lg hover:bg-opacity-70 transition duration-300 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <div className="flex items-center justify-center gap-3">
                  <FaUpload />
                  상품 업로드하기
                </div>
              </button>
            </form>
          </div>
        </div>
      </article>
    </section>
  );
};

export default ProductUploadForm;
